/**
 * Comment stripping that understands strings and regex literals.
 *
 * Lives in its own dependency-free module (same reason as `src/redos.ts` and
 * `src/forbidden-keys.ts`): `emitters/from-ts.ts` needs it, and importing it from
 * `lang/parser.ts` dragged the entire parser into the `tjs-browser-from-ts` bundle —
 * caught by `browser-bundle.test.ts` as a 23% size regression. Zero imports here, so any
 * consumer can use it without pulling a graph behind it.
 */

/**
 * Is the character at `index` escaped by a preceding backslash?
 *
 * **Count the run; do not look at one character.** `source[i - 1] !== '\\'` is the naive
 * form, and it is wrong for exactly the input that matters: in `'\\'` the character before
 * the closing quote IS a backslash, but that backslash is itself escaped, so the quote
 * closes the string. The naive check reads it as escaped, the scanner runs on past the end
 * of the literal, and everything downstream desynchronises — usually surfacing as a
 * baffling "Unexpected token" tens of characters later, and sometimes as a SILENT
 * mis-transpile with no error at all.
 *
 * This idiom was hand-rolled in fifteen scanners across five files. It was fixed in one of
 * them (bdfb847), whose own commit message noted the pattern recurs "in several scanners" —
 * so the remaining fourteen kept the bug. It lives here now, once.
 */
export function isEscapedAt(source: string, index: number): boolean {
  let backslashes = 0
  let k = index - 1
  while (k >= 0 && source[k] === '\\') {
    backslashes++
    k--
  }
  return backslashes % 2 === 1
}

/**
 * Is a `/` at this point the start of a REGEX rather than a division operator?
 *
 * Decided by the last significant character emitted so far: after a value (identifier,
 * number, `)`, `]`) a slash is division; otherwise it opens a regex. This is the standard
 * heuristic and is sufficient here — we only need to know how far to skip, so the rare
 * ambiguous case costs us nothing worse than today's behavior.
 */
export function isRegexStart(emitted: string): boolean {
  let j = emitted.length - 1
  while (j >= 0 && /\s/.test(emitted[j])) j--
  if (j < 0) return true // start of input
  const c = emitted[j]
  // A closing quote means a string literal just ended — strings are consumed whole, so a
  // trailing quote is always a VALUE, and `'a' // b` is a comment, not a regex.
  if (/[)\]'"`]/.test(c)) return false // (expr) / x, arr[0] / x, 'str' // comment
  if (/[A-Za-z0-9_$]/.test(c)) {
    // An identifier or number ends a value — UNLESS it is a keyword that expects an operand.
    const word = (emitted
      .slice(0, j + 1)
      .match(/[A-Za-z_$][A-Za-z0-9_$]*$/) || [''])[0]
    return REGEX_PRECEDING_KEYWORDS.has(word)
  }
  return true // after ( , = : [ ! & | ? { } ; and friends
}

/** Keywords after which a `/` opens a regex rather than dividing. */
const REGEX_PRECEDING_KEYWORDS = new Set([
  'return',
  'typeof',
  'instanceof',
  'in',
  'of',
  'new',
  'delete',
  'void',
  'throw',
  'case',
  'do',
  'else',
  'yield',
  'await',
])

/**
 * Index of the closing `/` of the regex literal starting at `start`, or -1.
 * Honours escapes and character classes, inside which `/` is a literal character.
 */
export function findRegexEnd(
  source: string,
  start: number,
  /**
   * The (position, state) pairs that scans for THIS source have already proven lead to
   * failure. A scan's future is fully determined by where it is and whether it is inside a
   * character class — every scan reads a backslash run from its start, so escapes align — so
   * a later scan that reaches a pair a failed scan passed through fails too, and stops there.
   * Each pair is then visited at most once over all scans: linear. Without it, one long line
   * of `/[/[…` (class never closes) or `/\\/\\…` (escaped slashes never close) made every
   * candidate `/` scan to the end of the line — quadratic in the lexical layer, before any
   * budget (0.14.0 final re-review 8's pairwise grid: ~2-4s at 60KB). Held equal to the plain
   * scan at every `/` of a corpus by strip-comments.test.ts.
   */
  memo?: RegexScanMemo
): number {
  let k = start + 1
  let inClass = false
  const failed = memo
    ? (memo.failed ??= new Uint8Array(source.length + 1))
    : null
  const visited: number[] = []
  const fail = () => {
    if (failed) for (const v of visited) failed[v >> 1] |= (v & 1) + 1
    return -1
  }
  while (k < source.length) {
    if (failed) {
      const state = inClass ? 2 : 1
      if (failed[k] & state) return fail()
      visited.push(k * 2 + (inClass ? 1 : 0))
    }
    const c = source[k]
    if (c === '\\') {
      k += 2
      continue
    }
    if (c === '\n') return fail() // regex literals cannot span lines
    if (inClass) {
      if (c === ']') inClass = false
    } else if (c === '[') {
      inClass = true
    } else if (c === '/') {
      return k
    }
    k++
  }
  return fail()
}

/** Per-source memo for {@link findRegexEnd}: bit 1 = failed outside a class, bit 2 = inside. */
export interface RegexScanMemo {
  failed?: Uint8Array
}

export function stripLineComments(source: string): string {
  // LENGTH-PRESERVING: a line comment is blanked to spaces, not removed.
  //
  // That is load-bearing and was nearly lost. `preprocess` runs this early and then works
  // in OFFSETS — doc-comment adjacency, brace matching, marker positions — so deleting the
  // text shifts everything after it. A first rewrite of this function removed the comments
  // instead, and the output looked right in isolation while a doc block stopped attaching
  // to the function below it, 200 lines away. The failing probe for that was itself wrong:
  // a hand-written "old implementation" that deleted rather than blanked, so it reported
  // agreement.
  //
  // Built on `blankRegions`, like the other views, because this was a fourth hand-rolled
  // literal walker living INSIDE the module written to end them — with no regex branch, so
  // a `//` inside `/[/]/` would have blanked the rest of the line.
  return blankRegions(source, (r) =>
    r.kind === 'line-comment' ? [r.start, r.end] : null
  )
}

/** A [start, end) range in the source, and what kind of region it is. */
export interface LiteralRegion {
  kind: 'string' | 'template' | 'regex' | 'line-comment' | 'block-comment'
  /** Offset of the opening delimiter. */
  start: number
  /** Offset just past the closing delimiter. */
  end: number
  /** Offset of the first character INSIDE the delimiters. */
  innerStart: number
  /** Offset just past the last character inside the delimiters. */
  innerEnd: number
}

/**
 * THE scanner. Every source-rewriting pass in this codebase should consume this rather
 * than hand-rolling its own literal tracking.
 *
 * One left-to-right pass classifying strings, templates, regex literals and comments. It
 * has to be one pass, not four, because the states are mutually exclusive and mutually
 * disambiguating: whether `/*` opens a comment depends on not being in a string, and
 * whether `'` opens a string depends on not being in a comment. Every scanner that tracked
 * only SOME of these got the others wrong.
 *
 * The bug class this exists to end, all of it shipped and all of it found in one release:
 *   - a `'/*'` in a string silently disabled every `test { }` block in the file — no error,
 *     no warning, no recorder entry, just zero tests where there were three
 *   - `'**\/*.ts'`, an ordinary glob, did the same
 *   - `const q = /['"]/` above a doc comment dropped an embedded test, and a `/'/` above a
 *     JSDoc promoted a documentation example into a real emitted test
 *   - `sep == '\\'` failed to transpile with "Unexpected token" 40 characters later
 *
 * None of these are exotic. They are what ordinary code looks like when it happens to
 * mention the syntax the scanner is scanning for — which source-processing code does
 * constantly, because it is code about code.
 */
/**
 * Bounded memo, keyed by the exact source string.
 *
 * Consolidating fifteen hand-rolled literal scanners onto this one was the right
 * correctness call and nothing memoized the result: one transpile calls it 175 times over
 * ~35 distinct strings — 91% redundant, ~745KB rescanned, 17% of total transpile time.
 * Callers legitimately ask repeatedly (each transform masks the source it was handed), and
 * telling every caller to hoist is the coordination problem this module exists to remove.
 *
 * There is NO staleness surface: the function is pure, so the same string always has the
 * same regions. The only cost is memory, which is why the cache is bounded and evicts in
 * insertion order rather than growing with every string a long-lived process ever sees.
 */
const SCAN_CACHE_MAX = 24
const scanCache = new Map<string, LiteralRegion[]>()

/** Drop the memo. Exposed for benchmarks and for hosts that want the memory back. */
export function clearLiteralCache(): void {
  scanCache.clear()
}

export function scanLiterals(source: string): LiteralRegion[] {
  const hit = scanCache.get(source)
  if (hit) {
    // Refresh recency so a string used throughout a transpile is not evicted by a burst
    // of one-off scans.
    scanCache.delete(source)
    scanCache.set(source, hit)
    return hit
  }
  // FROZEN, because the array is now shared between every caller for a given string.
  // Two exported wrappers hand it straight back, so a caller that sorted or spliced it
  // would silently corrupt the answer every later caller receives — a cache-poisoning
  // bug with no symptom at the mutation site. Frozen, that becomes an immediate throw.
  const computed = Object.freeze(
    scanLiteralsUncached(source)
  ) as LiteralRegion[]
  if (scanCache.size >= SCAN_CACHE_MAX) {
    const oldest = scanCache.keys().next().value
    if (oldest !== undefined) scanCache.delete(oldest)
  }
  scanCache.set(source, computed)
  return computed
}

/**
 * Index of the backtick that closes the template opening at `start`, or -1 if unterminated.
 *
 * The interior of a `${ … }` is CODE, and code can contain backticks — in a string, in a
 * comment, or as a nested template of its own. Finding the end therefore means tracking
 * substitution nesting, which is the one thing a "scan to the next delimiter" loop cannot do.
 *
 * This only changes where a template region ENDS. The region still spans the whole literal,
 * substitutions included, so what callers see masked is unchanged — a substitution's interior
 * stays blanked. Making it visible would be a different decision with a much wider blast
 * radius (every transform would start acting inside `${ … }`), and is not this.
 *
 * Regex literals inside a substitution are not tracked: deciding regex-vs-division needs the
 * preceding significant character, which this scan does not carry, and a regex containing a
 * backtick inside a template substitution is a long way down the tail. Named rather than
 * silently omitted, because an unstated limit is how the last one of these survived.
 */
function templateEnd(source: string, start: number): number {
  let k = start + 1
  let inSubstitution = false
  let braces = 0
  while (k < source.length) {
    const c = source[k]
    if (c === '\\') {
      k += 2
      continue
    }
    if (!inSubstitution) {
      if (c === '`') return k
      if (c === '$' && source[k + 1] === '{') {
        inSubstitution = true
        braces = 0
        k += 2
        continue
      }
      k++
      continue
    }
    // Inside `${ … }`: ordinary code, so skip anything that can hide a brace or a backtick.
    if (c === "'" || c === '"') {
      let j = k + 1
      while (j < source.length) {
        if (source[j] === '\\') {
          j += 2
          continue
        }
        if (source[j] === c) break
        j++
      }
      k = j + 1
      continue
    }
    if (c === '`') {
      const nested = templateEnd(source, k)
      k = nested === -1 ? source.length : nested + 1
      continue
    }
    if (c === '/' && source[k + 1] === '/') {
      const nl = source.indexOf('\n', k)
      k = nl === -1 ? source.length : nl
      continue
    }
    if (c === '/' && source[k + 1] === '*') {
      const close = source.indexOf('*/', k + 2)
      k = close === -1 ? source.length : close + 2
      continue
    }
    if (c === '{') braces++
    else if (c === '}') {
      if (braces === 0) inSubstitution = false
      else braces--
    }
    k++
  }
  return -1
}

function scanLiteralsUncached(source: string): LiteralRegion[] {
  const regions: LiteralRegion[] = []
  /** (position, state) pairs proven to fail (see findRegexEnd). */
  const regexMemo: RegexScanMemo = {}
  let i = 0
  let sigTail = ''
  while (i < source.length) {
    const ch = source[i]!
    if (ch === "'" || ch === '"' || ch === '`') {
      let j: number
      if (ch === '`') {
        // A template does NOT end at the next backtick — it ends at the next backtick that
        // is not inside one of its own `${ … }` substitutions. Scanning for a bare backtick
        // ended `` `${ x === "`" ? a : b }` `` at the quoted one, and the desync then
        // propagated: the code after it was masked and the literals after THAT were exposed
        // as code. Same defect as the bang-access transform's `${` arm, but in the shared
        // scanner, so every consumer inherited it.
        const close = templateEnd(source, i)
        j = close === -1 ? source.length : close
      } else {
        j = i + 1
        while (j < source.length) {
          if (source[j] === '\\') {
            j += 2
            continue
          }
          if (source[j] === ch) break
          j++
        }
      }
      regions.push({
        kind: ch === '`' ? 'template' : 'string',
        start: i,
        end: Math.min(j + 1, source.length),
        innerStart: i + 1,
        innerEnd: j,
      })
      i = j + 1
      sigTail = (sigTail + ch).slice(-24)
      continue
    }
    // ORDER MATTERS: `//` and `/*` are ALWAYS comments in JavaScript — an empty regex has
    // to be written `/(?:)/` — so the comment checks must precede the regex check.
    if (ch === '/' && source[i + 1] === '/') {
      const nl = source.indexOf('\n', i)
      const end = nl === -1 ? source.length : nl
      regions.push({
        kind: 'line-comment',
        start: i,
        end,
        innerStart: i + 2,
        innerEnd: end,
      })
      i = end
      continue
    }
    if (ch === '/' && source[i + 1] === '*') {
      const close = source.indexOf('*/', i + 2)
      const end = close === -1 ? source.length : close + 2
      regions.push({
        kind: 'block-comment',
        start: i,
        end,
        innerStart: i + 2,
        innerEnd: close === -1 ? source.length : close,
      })
      i = end
      continue
    }
    if (ch === '/' && isRegexStart(sigTail)) {
      const close = findRegexEnd(source, i, regexMemo)
      if (close !== -1) {
        regions.push({
          kind: 'regex',
          start: i,
          end: close + 1,
          innerStart: i + 1,
          innerEnd: close,
        })
        i = close + 1
        sigTail = '/'
        continue
      }
    }
    if (!/\s/.test(ch)) sigTail = (sigTail + ch).slice(-24)
    i++
  }
  return regions
}

/**
 * Split on top-level `,` — over the masked view, so a comma inside a literal is data.
 *
 * THE parameter splitter. There were three: `parser-params.ts` `splitParameters`,
 * `js-tests.ts` `splitParams`, and `dts.ts` `splitParams`. The first two were migrated onto
 * `maskLiterals` earlier in the 0.13.0 cycle, and the comment on the second names the first
 * as *the* sibling — so the third was never counted, and stayed literal-blind.
 *
 * What that cost is worse than the usual garbled output, because it reaches consumers as
 * BROKEN SYNTAX. `constructor(sep: ',', pad: 0)` emitted
 *
 *     constructor(sep: string, ': any, pad: number);
 *
 * — an unterminated string literal in the `.d.ts`. `tsc --noEmit` on it gives TS1002 twice
 * plus TS1003/TS1005/TS1138: a hard failure for every TypeScript consumer of the package,
 * from a single comma in an example value. Methods were corrupted the same way, not only
 * constructors.
 *
 * Returns raw slices, UNTRIMMED — callers that want trimmed elements say so, which is what
 * the three copies already disagreed about. A whitespace-only tail (from a trailing comma)
 * is dropped, since that is not a parameter.
 */
export function splitTopLevel(source: string, sep = ','): string[] {
  const masked = maskLiterals(source)
  const out: string[] = []
  let depth = 0
  let start = 0
  for (let i = 0; i < masked.length; i++) {
    const c = masked[i]
    if (c === '(' || c === '[' || c === '{') depth++
    else if (c === ')' || c === ']' || c === '}') depth--
    else if (c === sep && depth === 0) {
      out.push(source.slice(start, i))
      start = i + 1
    }
  }
  const tail = source.slice(start)
  if (tail.trim()) out.push(tail)
  return out
}

/**
 * `splitTopLevel`, trimmed, with empties dropped — which is how nearly every caller wants it.
 *
 * The splitter was extracted to stop a fourth copy of the depth-aware scan appearing, and
 * then two callers immediately wrote the SAME four-line wrapper around it
 * (`splitTopLevelTrimmed` in `dts.ts`, `splitParams` in `js-tests.ts`) — byte-equivalent
 * apart from the name and the parameter name. A consolidation that goes three-to-one and
 * grows two wrappers has gone three-to-three; the shared thing has to be the shape people
 * actually reach for.
 *
 * `splitTopLevel` stays exported for the one caller that genuinely wants the raw spans:
 * `parser-params.ts` splits a parameter list whose whitespace it later re-emits.
 */
export function splitTopLevelTrimmed(source: string, sep = ','): string[] {
  return splitTopLevel(source, sep)
    .map((p) => p.trim())
    .filter(Boolean)
}

/**
 * Index of the `}` matching the `{` at `open`, or -1.
 *
 * THE balanced-brace matcher. There were three, in `tests.ts`, `docs.ts` and
 * `parser-transforms.ts`, and they had already drifted in the two ways copies always do:
 *
 *   - **Return convention.** Two returned the index OF the closing brace; the third
 *     returned the index PAST it. Reading one and calling the other is an off-by-one that
 *     lands in the middle of the next construct.
 *   - **Who masks.** Two took a hoisted masked view as an argument, with docstrings citing
 *     the measurements that forced it (31% of transpile time for a 13KB file with 35
 *     tests; `generateDocs` on a 133KB file going 37.6ms → 538ms at 4× input, scaling as
 *     the square). The third re-masked internally at all six of its call sites, two of
 *     them inside loops — the exact quadratic the other two had already been fixed for.
 *
 * The masked view is a REQUIRED argument here, deliberately. Making it optional is what
 * let the third copy keep re-masking while looking like it had been migrated: a default
 * that silently does the expensive thing reads as convenience and behaves as a trap.
 * Masking preserves offsets, so the caller slices the ORIGINAL source with what this
 * returns.
 */
export function matchingBrace(masked: string, open: number): number {
  // The CLOSER comes from the OPENER, so this handles `{`, `[` and `(` alike.
  //
  // It was `{`-only, which is why a FOURTH private bracket scanner appeared in
  // `emitters/js-tests.ts` — in the same window that hoisted `splitTopLevelTrimmed`
  // specifically to stop that, and right next to the one CLAUDE.md and llms.txt both call
  // "the one balanced-brace matcher". Worse, the copy with no tests was the one the
  // headline nested-literal-unions feature depended on.
  //
  // Nesting is counted across ALL bracket kinds, so a `}` inside `[...]` cannot close an
  // outer `{`; a depth-zero closer of the wrong kind means the source is unbalanced, which
  // is -1 rather than a confident wrong answer.
  const CLOSERS: Record<string, string> = { '{': '}', '[': ']', '(': ')' }
  const want = CLOSERS[masked[open]]
  if (!want) return -1
  let depth = 0
  for (let i = open; i < masked.length; i++) {
    const c = masked[i]
    if (c === '{' || c === '[' || c === '(') depth++
    else if (c === '}' || c === ']' || c === ')') {
      depth--
      if (depth === 0) return c === want ? i : -1
    }
  }
  return -1
}

/**
 * The [start, end) ranges of every comment, string/regex-aware.
 *
 * `tests.ts` used to answer this by counting `/*` and `*\/` from the top of the file with
 * no notion of string literals, so a single `const OPEN = '/*'` convinced it that the rest
 * of the file was one enormous comment — and every `test { }` block after it vanished
 * silently. For a language whose thesis is that tests live in the source, reporting zero
 * tests instead of failing is the worst mode available.
 */
export function commentRanges(source: string): Array<[number, number]> {
  return scanLiterals(source)
    .filter((r) => r.kind === 'line-comment' || r.kind === 'block-comment')
    .map((r) => [r.start, r.end] as [number, number])
}

/**
 * Bounded memo per mask FLAVOUR, keyed by the source string.
 *
 * The scanner memo (`scanCache`) removed the re-SCANNING, but every caller still paid the
 * split -> blank -> join, which is the larger half for a big file: 200 masks of the same
 * 13KB source cost 21ms with the scan already cached. Transforms legitimately ask
 * repeatedly — each one masks the source it was handed — so the coordination problem is
 * the same one `scanLiterals` was memoized to remove, one layer up.
 *
 * Strings are immutable, so unlike the region arrays there is nothing a caller can corrupt
 * and no need to freeze. Pure function of the input; the only cost is memory, hence the
 * bound.
 */
const MASK_CACHE_MAX = 16
const maskCaches = new Map<string, Map<string, string>>()

function memoizedMask(flavour: string, source: string, compute: () => string) {
  let cache = maskCaches.get(flavour)
  if (!cache) {
    cache = new Map()
    maskCaches.set(flavour, cache)
  }
  const hit = cache.get(source)
  if (hit !== undefined) {
    cache.delete(source)
    cache.set(source, hit)
    return hit
  }
  const computed = compute()
  if (cache.size >= MASK_CACHE_MAX) {
    const oldest = cache.keys().next().value
    if (oldest !== undefined) cache.delete(oldest)
  }
  cache.set(source, computed)
  return computed
}

/** Drop every mask memo. Exposed alongside `clearLiteralCache` for benchmarks/hosts. */
export function clearMaskCache(): void {
  maskCaches.clear()
}

function blankRegions(
  source: string,
  pick: (r: LiteralRegion) => [number, number] | null
): string {
  const out = source.split('')
  for (const r of scanLiterals(source)) {
    const range = pick(r)
    if (!range) continue
    for (let k = range[0]; k < range[1] && k < out.length; k++) {
      if (out[k] !== '\n') out[k] = ' '
    }
  }
  return out.join('')
}

/**
 * Remove COMMENTS, keeping literal contents exactly as written.
 *
 * The third view, and the one that was missing. `maskLiterals` blanks literals AND
 * comments; `maskLiteralsKeepComments` blanks literals and keeps comments. Neither serves
 * a caller that wants comments GONE and literals INTACT — and one exists: the inline-test
 * harness matches `expect(...)` patterns outside comments, where the strings are the test
 * DESCRIPTIONS and blanking them erases the thing being extracted.
 *
 * Lacking the primitive, that caller hand-rolled it with two raw regexes, and carried a
 * comment saying so. Hand-rolled comment scanning is what cost 90 seconds of a 116-second
 * transpile — the module-directive detectors matched a leading comment run with an
 * alternation whose block-comment arm used a LAZY `[\s\S]*?`, which could stretch a
 * "comment" to any later close-marker in the file.
 *
 * (This doc comment originally quoted that pattern verbatim and terminated itself early,
 * which is the joke writing itself: a comment about comment-parsing, broken by
 * comment-parsing.)
 * A regex cannot decide whether `//` opens a comment — that depends on not being inside a
 * string, template or regex, which is exactly the state `scanLiterals` already tracks.
 *
 * Block comments become the same number of NEWLINES so line numbers survive; line comments
 * become nothing (their newline is not part of the region). Offsets therefore shift, unlike
 * the masking views — this returns a shorter string on purpose, for callers that only need
 * to pattern-match, not to index back.
 */
export function stripComments(source: string): string {
  const regions = scanLiterals(source).filter(
    (r) => r.kind === 'line-comment' || r.kind === 'block-comment'
  )
  if (regions.length === 0) return source
  let out = ''
  let at = 0
  for (const r of regions) {
    out += source.slice(at, r.start)
    if (r.kind === 'block-comment') {
      const text = source.slice(r.start, r.end)
      out += '\n'.repeat(text.split('\n').length - 1)
    }
    at = r.end
  }
  return out + source.slice(at)
}

/**
 * Blank the CONTENTS of strings, templates, regexes and comments, preserving length.
 *
 * Delimiters are kept so the result still tokenizes; only the insides become spaces. Since
 * every offset is unchanged, a regex scan over the mask yields indices that index straight
 * back into the original source.
 *
 * This exists because scanners that look for declarations with a plain regex find them
 * inside string literals too — `src/cli/create-app.ts` holds project-scaffolding templates
 * containing `function greet(...)`, and the polymorphic-dispatch detector treated two
 * unrelated scaffold examples as ambiguous variants of one function.
 *
 * Template interpolations are blanked along with the rest: a `function` DECLARATION inside
 * `${...}` is vanishingly rare, and treating one as a dispatch variant would be wrong
 * anyway.
 */
/**
 * Find TJS doc-comment spans: `/# … #/`, multi-line, nestable.
 *
 * ## Why TJS has its own doc comment at all
 *
 * `/*# … *\/` is an ordinary JavaScript block comment, which means it inherits JavaScript's
 * rule that block comments **do not nest** — so it terminates at the first `*\/` inside it.
 * A doc comment that cannot contain `*\/` cannot document comment syntax, which is precisely
 * what a language's own documentation needs to do. Escaping (`*\\/`) works and is what this
 * repo did, but an escape you must remember is a trap, and the failure is a parse error some
 * distance from the cause.
 *
 * ## Why `/# … #/` is safe to claim
 *
 * Single-line `/# x #/` is **valid JavaScript today** — a regex literal matching `#x#` — so
 * claiming it outright would make legal JS illegal and violate `PRINCIPLES.md` invariant 1.
 *
 * A regex literal cannot contain a raw newline. So a `/# … #/` span **containing a newline**
 * is already `Unterminated regular expression` — not legal JavaScript by any reading — and
 * claiming it takes nothing away from anyone. Measured, not estimated:
 *
 *     const re = /# comment #/          -> parses (regex)
 *     const re = /# one\n two #/         -> SyntaxError: Unterminated regular expression
 *
 * Hence the newline requirement below. It is not a style rule; it is the entire
 * subset-preservation argument, and a single-line `/#…#/` stays a regex forever.
 *
 * ## Why this scans the MASKED view
 *
 * Naively scanning raw text walks straight into this project's dominant defect class:
 *
 *     const a = /#/        // a legitimate single-line regex
 *     const s = "#/"       // a string that happens to contain #/
 *
 * A raw scan spans from the regex into the string and calls the result a doc comment. Over
 * `maskLiterals` it finds nothing, correctly.
 *
 * Nesting is depth-counted because we own the delimiter and nothing inside competes for it —
 * which is the whole point: a TJS doc comment can quote `/*`, `*\/`, `/*# … *\/`, and other
 * doc comments, with no escaping at all.
 */
/**
 * Characters the doc-comment scan has visited, for the linearity test
 * (`doc-comment-scan.test.ts`): work counted, not timed. Cheap enough to leave on.
 */
export const docScanWork = { steps: 0 }

export function findDocCommentSpans(source: string): Array<[number, number]> {
  // Scanned on RAW text, with only the OPENING position validated against the lexer.
  //
  // A doc comment is lexically outermost — it is a comment — so its CONTENT must not be
  // lexed. Masking first looks right and is not: in
  //
  //     /# docs
  //     write */ here #/
  //
  // the masker reads the `/` of that stray `*/` as a regex start and blanks ` here #`,
  // destroying the doc comment's own terminator. To know the `*/` is inside a doc comment you
  // must already know where the doc comment is, which is the thing being computed. So the
  // only lexing that happens here is on the OPENER: a `/#` that sits inside a string,
  // template or comment is not an opener.
  //
  // LINEAR (0.14.0-rc.10 review). It used to rescan from every opener: a failed one scanned
  // toward EOF and the outer loop advanced by one, so `'/#'.repeat(k)` was quadratic, and
  // pre-fuel on the AJS path. Now the scan that STARTS at a position is computed once:
  // `match(p)` is what scanning from just after an opener finds, and a nested opener's scan is
  // the same scan as its parent's from that point, so the parent reuses it rather than walking
  // it again. The result is identical to the old one (oracle in doc-comment-scan.test.ts).
  const regions = scanLiterals(source)
  // Queries arrive in increasing offset order, so a cursor replaces `regions.some`.
  let cursor = 0
  const insideLiteral = (offset: number): boolean => {
    while (cursor < regions.length && regions[cursor].end <= offset) {
      cursor++
      docScanWork.steps++
    }
    const r = regions[cursor]
    return r !== undefined && offset >= r.start && offset < r.end
  }

  /** Scanning from `p` (just after an opener) to the close that ends it. */
  type Match = { end: number; newline: boolean; earlyClose: boolean } | null
  const memo = new Map<number, Match>()
  const last = source.length - 1
  const match = (start: number): Match => {
    if (memo.has(start)) return memo.get(start)!
    // An explicit stack: nesting depth is input-controlled.
    type Frame = {
      p: number
      j: number
      newline: boolean
      closed: boolean
      early: boolean
    }
    const stack: Frame[] = [
      { p: start, j: start, newline: false, closed: false, early: false },
    ]
    let result: Match = null
    let returning = false
    while (stack.length) {
      const f = stack[stack.length - 1]
      if (returning) {
        // A nested scan just finished: continue the parent after it.
        returning = false
        if (result === null) {
          memo.set(f.p, null)
          stack.pop()
          returning = true
          continue
        }
        // The FIRST close of the whole scan is the nested scan's first close, unless one was
        // already seen; it was single-line if no newline came before it.
        if (!f.closed) {
          f.closed = true
          f.early = !f.newline && result.earlyClose
        }
        f.newline = f.newline || result.newline
        f.j = result.end
      }
      let pushed = false
      while (f.j < last) {
        docScanWork.steps++
        const c = source[f.j]
        if (c === '\n') f.newline = true
        if (c === '/' && source[f.j + 1] === '#') {
          const sub = f.j + 2
          if (memo.has(sub)) {
            result = memo.get(sub)!
            returning = true
            pushed = true
            break
          }
          stack.push({
            p: sub,
            j: sub,
            newline: false,
            closed: false,
            early: false,
          })
          pushed = true
          break
        }
        if (c === '#' && source[f.j + 1] === '/') {
          if (!f.closed) {
            f.closed = true
            f.early = !f.newline
          }
          result = { end: f.j + 2, newline: f.newline, earlyClose: f.early }
          memo.set(f.p, result)
          stack.pop()
          returning = true
          pushed = true
          break
        }
        f.j++
      }
      if (!pushed) {
        // EOF before the close: this scan, and every scan waiting on it, fails.
        result = null
        memo.set(f.p, null)
        stack.pop()
        returning = true
      }
    }
    return memo.get(start)!
  }

  const spans: Array<[number, number]> = []
  let i = 0
  while (i < last) {
    docScanWork.steps++
    if (source[i] !== '/' || source[i + 1] !== '#' || insideLiteral(i)) {
      i++
      continue
    }
    const m = match(i + 2)
    if (m === null || !m.newline || m.earlyClose) {
      i++
      continue
    }
    spans.push([i, m.end])
    i = m.end
  }
  return spans
}

/**
 * The markdown inside each TJS doc comment, delimiters removed.
 *
 * This is what makes a `.tjs` file a literate program on its own terms: the documentation is
 * IN the language, so a doc system renders documentation it does not define. Nothing about
 * publishing a `.tjs` file's docs requires a particular build tool.
 *
 * Nested doc comments are returned as part of their parent's text rather than separately —
 * a `/# … #/` inside another is being QUOTED (that is the entire reason nesting exists), so
 * lifting it out would publish an example as though it were a document.
 */
export function extractDocComments(source: string): string[] {
  return findDocCommentSpans(source).map((span) =>
    source.slice(span[0] + 2, span[1] - 2).trim()
  )
}

/**
 * Drop trailing spaces and tabs from every line, EXCEPT inside a string or template literal.
 *
 * The passes that blank a region (doc comments, test blocks, the hashbang) replace it with
 * spaces so offsets hold while the source is being rewritten. In the OUTPUT that padding is
 * dead weight: 100 blanked comment lines took a 3.5KB module to ~10KB (0.14.0-rc.4 pre-tag
 * review m1). Only TRAILING whitespace goes, so every line and every column that has code on
 * it is unchanged; a blanked span followed by code on its last line keeps the spaces in
 * front of that code, because they are leading, not trailing.
 *
 * Literal-aware because a template literal's trailing spaces are its value — a global
 * `/[ \t]+$/gm` would change what the program prints.
 */
export function trimTrailingWhitespace(code: string): string {
  const keep = scanLiterals(code).filter(
    (r) => r.kind === 'template' || r.kind === 'string'
  )
  // A linear scan, not `/[ \t]+(?=\n|$)/g`: that regex restarts at every space of a long run
  // that ends in code, which is quadratic on exactly the blanked lines this exists for.
  const parts: string[] = []
  let last = 0
  let k = 0
  let runStart = -1
  for (let i = 0; i <= code.length; i++) {
    const ch = i < code.length ? code[i] : '\n'
    if (ch === ' ' || ch === '\t') {
      if (runStart < 0) runStart = i
      continue
    }
    if (ch === '\n' && runStart >= 0) {
      while (k < keep.length && keep[k].innerEnd <= runStart) k++
      const inLiteral = k < keep.length && keep[k].innerStart <= runStart
      if (!inLiteral) {
        parts.push(code.slice(last, runStart))
        last = i
      }
    }
    runStart = -1
  }
  if (last === 0) return code
  parts.push(code.slice(last))
  return parts.join('')
}

/** Replace every TJS doc comment with equivalent whitespace, preserving offsets. */
export function blankDocComments(source: string): string {
  const spans = findDocCommentSpans(source)
  if (!spans.length) return source
  // One pass (rc.10 review): rebuilding the string once per span was quadratic in the number
  // of doc comments.
  const parts: string[] = []
  let at = 0
  for (const [start, end] of spans) {
    docScanWork.steps += end - at
    parts.push(
      source.slice(at, start),
      source.slice(start, end).replace(/[^\n]/g, ' ')
    )
    at = end
  }
  parts.push(source.slice(at))
  return parts.join('')
}

export function maskLiterals(source: string): string {
  return memoizedMask('literals', source, () =>
    blankRegions(source, (r) =>
      r.kind === 'line-comment' || r.kind === 'block-comment'
        ? [r.start, r.end] // comments vanish entirely, delimiters included
        : [r.innerStart, r.innerEnd]
    )
  )
}

/**
 * Blank string, template and regex contents but LEAVE COMMENTS INTACT.
 *
 * The view for anything that extracts FROM comments — embedded `/*test … *\/` blocks, doc
 * comments, `@tjs` annotations. Those passes cannot use `maskLiterals` (it erases the very
 * thing they are reading) and so each hand-rolled its own partial scanner and got regex
 * literals wrong: `const q = /['"]/` above a test comment dropped the test, and `const q =
 * /'/` above a JSDoc promoted a documentation example into a real emitted test — a false
 * negative and a false positive from the same blind spot.
 */
export function maskLiteralsKeepComments(source: string): string {
  return memoizedMask('keep-comments', source, () =>
    blankRegions(source, (r) =>
      r.kind === 'line-comment' || r.kind === 'block-comment'
        ? null
        : [r.innerStart, r.innerEnd]
    )
  )
}

/**
 * Find `unsafe <expression>` spans.
 *
 * `unsafe` is the language's per-construct escape: it says "this construct, deliberately"
 * at the site where the exception lives, so the rules themselves stay unconditional and the
 * file extension remains the only gate. It replaces per-file mode dialing, which had to
 * disable a rule for a whole file and therefore also silenced the NEXT, accidental use.
 *
 * Recognised only as `unsafe` + whitespace + the start of an identifier, so a variable
 * named `unsafe`, a call `unsafe(x)`, and `unsafe.foo` are all left alone.
 *
 * Returns [start, end) spans covering the keyword AND its expression.
 */
export function findUnsafeSpans(source: string): Array<[number, number]> {
  const spans: Array<[number, number]> = []
  const masked = maskLiterals(source)
  // SAME LINE only — `[ \t]`, not `\s`. `unsafe foo()` on one line is not valid
  // JavaScript (two juxtaposed expressions), so it can only be the marker. Across a
  // newline it IS valid — ASI makes `let r = unsafe` / `foo()` two statements — so a
  // variable named `unsafe` at end of line must not be swallowed. Same ASI hazard every
  // JS developer already knows from `return`.
  //
  // The negative lookahead is the other half of that argument, and it is load-bearing:
  // `unsafe` followed by a WORD-SHAPED INFIX OPERATOR is an ordinary variable being
  // operated on, not a marker. `unsafe instanceof Function` and `unsafe in obj` are both
  // legal JavaScript, and without this they were swallowed as `unsafe <expr>` and the file
  // failed to parse — legal JS made uncompilable, which is a TJS ⊇ JS violation
  // (PRINCIPLES.md), and it fired in `dialect: 'js'` too, where there is no escape hatch.
  const re = /\bunsafe[ \t]+(?!(?:instanceof|in|of)\b)(?=[A-Za-z_$])/g
  let m: RegExpExecArray | null
  while ((m = re.exec(masked)) !== null) {
    // Member position is never a marker: `obj.unsafe`, `obj?.unsafe`. `isRegexStart` alone
    // does NOT cover this — after a `.` it falls through to its permissive default and
    // returns true, so `o.unsafe instanceof Function` was read as a marker.
    if (lastSignificantChar(masked, m.index) === '.') continue
    // Expression-prefix position only. Reserving the word outright would be simpler but
    // would make legal JavaScript illegal, breaking TJS ⊇ JS (PRINCIPLES.md).
    if (!isRegexStart(masked.slice(0, m.index))) continue
    const exprStart = m.index + m[0].length
    spans.push([m.index, unsafeExpressionEnd(masked, exprStart)])
  }
  return spans
}

/**
 * The last non-whitespace character before `index`, or `''` at the start of input.
 *
 * Safe to run on a literal-masked source only: `maskLiterals` blanks comments entirely
 * (delimiters included), so skipping whitespace also skips comments.
 */
function lastSignificantChar(masked: string, index: number): string {
  let j = index - 1
  while (j >= 0 && /\s/.test(masked[j]!)) j--
  return j >= 0 ? masked[j]! : ''
}

/**
 * End of the expression guarded by `unsafe`, starting at `at`.
 *
 * Consumes a normal expression — identifiers, member access, calls, `new` — tracking
 * bracket depth, and stops at the first top-level `,` `;` or closing bracket, or at a
 * newline that is not inside brackets. That covers both `const d = unsafe new Date(ts)` and
 * `f(unsafe Date.now(), x)`.
 */
function unsafeExpressionEnd(masked: string, at: number): number {
  let i = at
  let depth = 0
  while (i < masked.length) {
    const c = masked[i]
    if (c === '(' || c === '[' || c === '{') depth++
    else if (c === ')' || c === ']' || c === '}') {
      if (depth === 0) break
      depth--
    } else if (depth === 0 && (c === ',' || c === ';')) break
    else if (depth === 0 && c === '\n') break
    i++
  }
  return i
}

/**
 * Blank `unsafe <expression>` spans, preserving offsets — the view the rule checks see.
 * A construct the author has explicitly taken responsibility for is not a violation.
 *
 * **The mask stops at a nested function body.** `unsafe` exempts ONE CONSTRUCT, not a
 * region — that is the whole reason it replaced per-file mode dialing, which silenced the
 * next, accidental use as well. But a bracketed expression can contain an arbitrary amount
 * of unrelated authored code:
 *
 *     unsafe makeHandler({ onClick: () => { eval(src); var leaked = 1; new Date() } })
 *
 * Masking that whole span exempts three violations the author never took responsibility
 * for, and the marker on the OUTER call is not a statement about the inside of a callback.
 * So the mask covers the construct up to the point where a new function body begins, and
 * everything inside that body is checked normally. Code that genuinely needs an exemption
 * in there can carry its own `unsafe`, which is exactly the intended ergonomics.
 */
export function maskUnsafe(source: string): string {
  const out = source.split('')
  for (const [a, b] of unsafeRuleSpans(source)) {
    for (let k = a; k < b && k < out.length; k++) {
      if (out[k] !== '\n') out[k] = ' '
    }
  }
  return out.join('')
}

/**
 * The [start, end) ranges a rule check should treat as exempt — `findUnsafeSpans` narrowed
 * to stop at any nested function body.
 *
 * Exported because the LINTER needs the same answer as the compiler and previously did not
 * have it: `lint('const d = unsafe new Date(0)')` reported `no-explicit-new` on source that
 * `tjs()` compiled without complaint. The linter drives playground and editor diagnostics,
 * so that disagreement is what a user sees FIRST — the compiler's own documented remedy,
 * underlined as a mistake. One function, two consumers, no chance to drift.
 */
export function unsafeRuleSpans(source: string): Array<[number, number]> {
  const masked = maskLiterals(source)
  return findUnsafeSpans(source).map(([a, b]) => [
    a,
    Math.min(b, nestedFunctionBodyStart(masked, a, b)),
  ])
}

/**
 * Offset of the `{` opening the first nested function body within [a, b), else `b`.
 *
 * Recognises the two ways a body can open: after `=>`, and after the parameter list of a
 * `function` expression. Anything else (an object literal, a block) is part of the guarded
 * construct itself and stays masked.
 */
function nestedFunctionBodyStart(masked: string, a: number, b: number): number {
  for (let i = a; i < b; i++) {
    if (masked[i] !== '{') continue
    const before = masked.slice(a, i)
    // Arrow body: `… => {`
    if (/=>\s*$/.test(before)) return i
    // Function expression body: `function [name] (params) {`, allowing for the params
    // spanning lines. The `[\s\S]` is deliberate — a formatted parameter list wraps.
    if (/\bfunction\b[\s\S]*\)\s*$/.test(before)) return i
  }
  return b
}

/**
 * Remove the `unsafe` keyword, leaving the expression. Zero runtime cost: the marker is
 * a compile-time assertion of intent, not a wrapper.
 */
export function stripUnsafeMarkers(source: string): string {
  const spans = findUnsafeSpans(source)
  if (!spans.length) return source
  const out = source.split('')
  for (const [a] of spans) {
    // Blank just the keyword and its trailing whitespace; offsets are preserved so every
    // position reported by later stages still lines up with the author's source.
    const kw = /^unsafe\s+/.exec(source.slice(a))
    if (kw) for (let k = a; k < a + kw[0].length; k++) out[k] = ' '
  }
  return out.join('')
}

/**
 * The `#!` line a source begins with, WITHOUT its newline — or `''` if there is none.
 *
 * ONE scanner. There were three: `parser.ts` (which blanks it), `emitters/js.ts` (which
 * reports it), and `cli/commands/convert.ts` (which captures it from the original
 * TypeScript, because the TS→TJS→JS chain loses it at the first step). Two were
 * byte-identical and the third differed only in what it did with the answer.
 *
 * Three copies of "where does the hashbang end" is three chances to disagree about a
 * one-line question, in a release whose recurring defect is a rule applied at one site and
 * not its siblings.
 */
export function hashbangOf(source: string): string {
  if (!source.startsWith('#!')) return ''
  const nl = source.indexOf('\n')
  return source.slice(0, nl === -1 ? source.length : nl)
}

/**
 * Make arbitrary text safe to embed inside a generated block comment.
 *
 * JavaScript block comments do not nest: the first terminator ends the comment, whatever was
 * intended. So interpolating source text into a diagnostic comment is only safe if that text
 * cannot itself contain a terminator — and TypeScript type text routinely does, because a
 * member can carry its own doc comment:
 *
 *     params: Omit<Extract<Part, { type: Type }>, ...> & {
 *       [doc comment here]
 *       readonly options?: ...
 *     }
 *
 * Embedded verbatim, that inner terminator closed the diagnostic early and the remainder of
 * the type became stray code. effect's Prompt.ts and Response.ts both failed to parse on it,
 * and the irony is the point: a comment whose purpose is to EXPLAIN a degraded conversion is
 * what broke the file.
 *
 * Same family as the literal-blindness defects this module exists for, one level out — there a
 * pass misreads code that mentions its syntax, here a pass emits code that terminates its own.
 * (Writing this docstring hit it a third way: spelling the terminator out to illustrate it
 * would have ended this comment, and spelling it with a zero-width space tripped ESLint's
 * no-irregular-whitespace. Hence the placeholder above.)
 *
 * The replacement inserts a space rather than an escape or an invisible character, because the
 * result is read by humans and must stay legible.
 */
export function commentSafe(text: string): string {
  return text.replace(/\*\//g, '* /')
}
